import React from "react";
import {
  Col,
  Logo,
  textVr,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiInput,
  UiInputGroup,
  UiInputLeftElement,
  UiLink,
  UiPrimaryButton,
  UiText,
} from "~/components/elements";
import { MailIcon } from "~/components/icons/SvgIcons";
import { Link } from "react-router-dom";
import { PAGE_URLS } from "~/constants";
import { useAppDispatch, useInput, useNotification, useRequestState } from "~/hooks";
import { sendMailResetPwd } from "~/redux/auth/thunk";
import { requestOK } from "redux-thunk-kit";
import { unwrapResult } from "@reduxjs/toolkit";

export function InputEmail({
  onSuccess,
}: {
  onSuccess: ({ email, userId }: { email?: string; userId: string }) => void;
}) {
  const dispatch = useAppDispatch();
  const { addMessage } = useNotification(dispatch);

  const rules = { email: { presence: true, email: { message: "^Please enter a valid email address" } } };
  const [inputs, setInputs, { validation }] = useInput({ email: "" }, { rules });

  const { loading } = useRequestState(sendMailResetPwd);

  const handleSubmit = async (e: React.FormEvent) => {
    e?.preventDefault();
    if (validation.getErrors()) {
      return;
    }
    const resAction = await dispatch(sendMailResetPwd({ data: { email: inputs.email } }));
    if (requestOK(resAction, sendMailResetPwd)) {
      const result = unwrapResult(resAction);
      onSuccess({ email: inputs.email, userId: result.apiResponseData.user_id });
    }
  };

  return (
    <Col mt={8} px={{ base: 4 }} maxW="29.625rem" w="full">
      <Logo containerProps={{ justifyContent: "center" }} />
      <UiText vr="headline3" fontSize={{ base: "1.5rem", md: "1.75rem" }} textAlign="center" mt={12}>
        Forgot password
      </UiText>
      <UiText vr="body1" textAlign="center" mt={3}>
        We will send account recovery details to the email you provided
      </UiText>
      <form onSubmit={handleSubmit}>
        <Col mx="auto" mt={5}>
          <UiFormControl isRequired isInvalid={!!validation?.errors?.email}>
            <UiFormLabel {...textVr("body2")}>Email</UiFormLabel>
            <UiInputGroup>
              <UiInputLeftElement pointerEvents="none">
                <MailIcon />
              </UiInputLeftElement>
              <UiInput
                ps="10"
                placeholder="Enter your email"
                name="email"
                type="email"
                _invalid={{ boxShadow: "none", borderColor: "red" }}
                value={inputs.email}
                onChange={({ target }) => setInputs({ email: target.value })}
              />
            </UiInputGroup>
            {validation?.errors?.email?.[0] && (
              <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                {validation?.errors?.email?.[0]}
              </UiFormErrorMessage>
            )}
          </UiFormControl>
          <UiPrimaryButton isLoading={loading} type="submit" onClick={handleSubmit} isDisabled={!inputs.email} mt={4}>
            Reset password
          </UiPrimaryButton>
        </Col>
      </form>
      <UiLink as={Link} to={{ pathname: PAGE_URLS.LOGIN, search: window.location.search }} mx="auto" mt={4}>
        <UiText vr="title1" color="navy">
          Return to Login
        </UiText>
      </UiLink>
      <UiText vr="body2" mx="auto" mt={5}>
        Don&apos;t have an account?
        <UiLink as={Link} to={{ pathname: PAGE_URLS.REGISTER, search: window.location.search }} fontWeight="700">
          &nbsp;Sign up for free&nbsp;
        </UiLink>
      </UiText>
    </Col>
  );
}
